import React from 'react';

import { EditOutlined } from '@ant-design/icons';
import { Button } from 'antd';
import { type ColumnsType } from 'antd/lib/table';
import UserStatusTag from 'components/user-status-tag';
import { BASE_URL, USER_AVATAR_FOLDER } from 'config';
import dayjs from 'dayjs';
import { type Role, StatusList, type User } from 'models/user';
import { Link } from 'react-router-dom';

import { type FiltersState } from './filters';
import { Avatar, RoleTag } from '../../../index';
import { type ExtraColumn } from '../ui/view';

export interface DataType {
  key: React.Key;
  name: string;
  login: string;
  email: string;
  role: Role;
  isActive: boolean;
  teamLeads?: Pick<User, 'id' | 'name'>[];
  createdAt: string;
}

interface Props {
  filters: FiltersState;
  userRoleFilterValues: Role[];
  extraColumns?: ExtraColumn[];
  setUser?: (user: User) => void;
  onEditClick?: VoidFunction;
}

export const useColumns = ({
  filters,
  userRoleFilterValues,
  extraColumns,
  setUser,
  onEditClick,
}: Props): ColumnsType<DataType> => {
  const columns: ColumnsType<DataType> = [
    {
      title: 'Фото',
      dataIndex: 'avatar',
      key: 'avatar',
      align: 'center',
      width: 90,
      render: (value) => {
        const src = value ? `${BASE_URL}${USER_AVATAR_FOLDER}/${value}` : undefined;
        return <Avatar src={src} />;
      },
    },
    {
      title: 'Имя',
      dataIndex: 'name',
      key: 'name',
      sorter: true,
      sortOrder: filters.sort.columnKey === 'name' ? filters.sort.order : null,
      render: (value, record) => <Link to={`/users/${Number(record.key)}`}>{value}</Link>,
    },
    {
      title: 'Login',
      dataIndex: 'login',
      key: 'login',
      sorter: true,
      sortOrder: filters.sort.columnKey === 'login' ? filters.sort.order : null,
    },
    {
      title: 'Email',
      dataIndex: 'email',
      width: 220,
      key: 'email',
    },
    {
      title: 'Должность',
      dataIndex: 'role',
      key: 'role',
      align: 'center',
      filters: userRoleFilterValues.map((role) => ({ text: role, value: role })),
      filteredValue:
        filters.filterInfo.role && filters.filterInfo.role.length > 0 ? filters.filterInfo.role : null,
      render: (value) => <RoleTag roleType={value} />,
    },
    {
      title: 'Тимлид',
      dataIndex: 'teamLead',
      key: 'teamLead',
      render: (value, record) => (record?.teamLeads?.some((tl) => tl.id === record.key) ? null : value),
    },
    {
      title: 'День рождения',
      dataIndex: 'birthday',
      width: 140,
      key: 'birthday',
      render: (value) => (value ? dayjs(value).format('DD-MM-YYYY') : null),
    },
    {
      title: 'Активность',
      dataIndex: 'isActive',
      key: 'isActive',
      align: 'center',
      filters: StatusList.map((status) => ({ text: status, value: status })),
      filteredValue: filters.filterInfo.isActive || null,
      render: (value) => <UserStatusTag isActive={value} />,
    },
    {
      title: 'Добавлен',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (value) => dayjs(value).format('DD-MM-YYYY HH:MM:ss'),
      sorter: true,
      sortOrder: filters.sort.columnKey === 'createdAt' ? filters.sort.order : null,
    },
    {
      title: '',
      width: 68,
      dataIndex: 'edit',
      key: 'edit',
      align: 'center',
      render: (value, record) => (
        <Button
          icon={<EditOutlined />}
          onClick={() => {
            if (setUser)
              setUser({
                id: Number(record.key),
                ...record,
                experience: [],
              });
            if (onEditClick) onEditClick();
          }}
        />
      ),
    },
  ];

  const mainColumns: string[] = ['avatar', 'name', 'login', 'role', 'birthday', 'email'].concat(
    extraColumns ?? []
  );

  const filteredColumns = columns.filter((col) => mainColumns.includes(col.key as string));

  return filteredColumns;
};
